import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Modal,
  Fade,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import { Divider, Stack, Box } from "@mui/material";
import Button from "../Button";
import Warning from "@mui/icons-material/Warning";
import MuiTooltip from "../MuiTooltip";
import Input from "../Input";
import LockStepsSummary from "../LockStepsSummary";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: "40%",
    maxHeight: "80%",
    overflowY: "auto",
    borderRadius: 10,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      width: "auto",
    },
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "16px",
    lineHeight: "20px",
    marginBottom: "20px",
    borderRadius: "4px",
    border: "1px solid #E1E1E1", // Default border
    "&:focus": {
      outline: "none",
      borderColor: "#ccc", // Optional: change border color on focus
    },
  },
  visibilityToggle: {
    cursor: "pointer",
    position: "absolute",
    right: "10px",
    top: "10px",
  },
  errorText: {
    color: "red",
    fontSize: "14px",
    marginTop: "8px",
  },
  noUppercase: {
    textTransform: "none",
    color: "#565556",
    fontFamily: "DM Sans",
    fontSize: "16px",
  },
}));

const AccessKeyEnterModal = ({
  isOpen,
  handleClose,
  onSubmit = () => {},
  loading = false,
  error = "",
  setError = () => {},
  isShowCancel = false,
  description = "To unlock this Wheel, please enter the password.",
  // ISP-9999 (fix-editwheel-unlock-prompt-silent-no-op): which lock is
  // ACTUALLY blocking this visitor. Default "access_code" preserves today's
  // only prompt for every existing caller that doesn't pass this. An "email"
  // (permission_lock) lock has NO password to submit — showing the password
  // field + "Enter Password" copy in that case was a dead end (no password
  // exists, and no "request access" path was offered).
  lockKind = "access_code",
  // ISP-9999 (gordon-0925-P0-lock-rules-fair-order): the BE's ordered step
  // summary (upfront list + "Step X of N"). null/undefined on a flag-off BE or
  // a single-lock wheel -- LockStepsSummary renders nothing in that case.
  lockSteps = null,
}) => {
  const classes = useStyles();
  const [password, setPassword] = useState("");
  const isEmailGrantLock = lockKind === "email";

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    error && setError("");
  };

  const onButtonPress = () => {
    onSubmit(password);
    setPassword("");
    setError("");
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onButtonPress();
  };

  const errorId = error ? "access-key-enter-error" : undefined;

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      className={classes.modal}
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      // MODAL-BACKDROP: do NOT pass `BackdropComponent={Backdrop}`. MUI's default
      // slot is `styled(Backdrop)({ zIndex: -1 })`; handing it the raw Backdrop
      // drops that rule and the backdrop paints OVER this dialog, eating the
      // Confirm click (measured on live staging). See utils/theme.js MuiModal.
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={isOpen}>
        <div className={classes.paper}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Warning
                style={{ color: "rgba(245, 204, 59, 1)", marginRight: "8px" }}
              />
              <Typography
                variant="h6"
                id="transition-modal-title"
                style={{
                  color: "#565556",
                  fontFamily: "DM Sans",
                  fontWeight: 700,
                  fontSize: "20px",
                  lineHeight: "20px",
                  marginRight: "5px",
                }}
              >
                {/* ISP-9999: an "email" (permission_lock) lock has no
                    password to enter at all — "Enter Password" here was a
                    dead-end title for a visitor who was never going to have
                    one. */}
                {isEmailGrantLock ? "Restricted Access" : "Enter Password"}
              </Typography>

              <MuiTooltip
                message={
                  isEmailGrantLock
                    ? "Ask the wheel owner to grant your email access"
                    : "Request Password from Wheel Owner"
                }
                size="medium"
                sx={{ marginTop: 0.5 }}
              />
            </Box>
          </Stack>
          <Divider sx={{ mt: 2 }} variant="middle" />
          <LockStepsSummary steps={lockSteps} />
          <Stack direction={"row"} alignItems={"center"} gap={2} my={2}>
            <Typography
              style={{
                fontFamily: "DM Sans",
                fontSize: 16,
                fontWeight: "500",

                color: "#565556",
              }}
            >
              {isEmailGrantLock
                ? "This Wheel is only available to people the owner has granted access to. Ask the owner to add your email to the list, or open the access link they send you — there is no password to enter."
                : description}
            </Typography>
          </Stack>
          {error && (
            <Typography
              id={errorId}
              role="alert"
              style={{
                fontFamily: "DM Sans",
                fontSize: 12,
                fontWeight: "500",
                color: "red",
                marginBottom: 10,
              }}
            >
              {error}
            </Typography>
          )}
          {/* ISP-9999: no password input (and no Confirm submission) for an
              email-grant-only lock — there is nothing valid a visitor could
              type here, so offering the field was the "silent no-op" trap:
              any guess just bounced with a generic/no error. */}
          {!isEmailGrantLock && (
            <>
              <Typography
                style={{
                  fontFamily: "DM Sans",
                  fontSize: 12,
                  fontWeight: "500",
                  color: "#123051",
                  marginBottom: 10,
                }}
              >
                Enter Password
              </Typography>
              <div style={{ position: "relative", marginBottom: 20 }}>
                <form onSubmit={handleFormSubmit} autoComplete="off">
                  {/* The shared Input renders its own wired show/hide toggle for
                      type="password" (eye icon, keyboard-accessible) — no local
                      visibility state needed here. */}
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => handlePasswordChange(e)}
                    name="password"
                    variant={"secondary"}
                    placeholder="Password"
                    containerStyle={{ marginBottom: 0 }}
                    aria-describedby={errorId}
                    aria-invalid={error ? true : undefined}
                  />
                </form>
              </div>
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            {isShowCancel && (
              <MuiButton
                variant="text"
                onClick={() => handleClose()}
                className={classes.noUppercase}
              >
                Go Back
              </MuiButton>
            )}
            {!isEmailGrantLock && (
              <Box>
                <Button
                  isLoading={loading}
                  onClick={() => onButtonPress()}
                  sx={{ mt: 10 }}
                >
                  Confirm
                </Button>
              </Box>
            )}
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};

export default AccessKeyEnterModal;
